/**
 * Jinggangshan secondary-development module.
 * Scope: fix "copy chat content -> pasted into Word shows dark background with
 * white text".
 *
 * Why it happens: the app defaults to the dark theme and sets
 *   body { background-color: var(--bg-base) /* #161618 *\/; color: var(--text-primary) }
 * When the user selects text with the mouse and presses Ctrl+C, the browser also
 * writes the *computed* background/color of the surrounding block into the
 * clipboard's text/html flavour. Microsoft Word and WPS honour those, producing
 * an unreadable dark block.
 *
 * What we do: install ONE capture-phase `copy` listener. When (and only when) the
 * selection lives inside a container marked with JGS_CLEAN_COPY_ATTR, we rewrite
 * the clipboard ourselves — plain text stays plain text, and the HTML flavour is
 * stripped of inline background/colour and wrapped in our light export stylesheet.
 *
 * Additive-only: the dark theme itself is untouched; copying outside chat is untouched.
 */
import { useEffect } from 'react';

import {
  JGS_CLEAN_COPY_ATTR,
  JGS_CLEAN_COPY_SELECTOR,
  JGS_EXPORT_CSS,
  JGS_STRIP_STYLE_PROPS,
} from '@/utils/jgs-export-css';

const INSTALL_FLAG = '__jgsCleanCopyInstalled';

/** 判断选区是否落在受管的聊天内容区。 */
function selectionRoot(): HTMLElement | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) {
    return null;
  }
  const node = sel.getRangeAt(0).commonAncestorContainer;
  const el =
    node && node.nodeType === Node.ELEMENT_NODE
      ? (node as HTMLElement)
      : (node?.parentElement ?? null);
  if (!el || typeof el.closest !== 'function') {
    return null;
  }
  return el.closest(JGS_CLEAN_COPY_SELECTOR) as HTMLElement | null;
}

/** 去掉元素上的暗色内联样式，保留语义标签（标题/表格/列表/代码）。 */
function sanitize(root: HTMLElement): void {
  const nodes = [root, ...Array.from(root.querySelectorAll<HTMLElement>('*'))];
  nodes.forEach((el) => {
    const style = (el as HTMLElement).style;
    if (!style || style.length === 0) {
      return;
    }
    JGS_STRIP_STYLE_PROPS.forEach((prop) => style.removeProperty(prop));
    if (style.length === 0) {
      el.removeAttribute('style');
    }
  });
  // 移除纯装饰属性，避免把编辑器残留标记带进 Word
  root.querySelectorAll('[contenteditable]').forEach((el) => el.removeAttribute('contenteditable'));
  root.querySelectorAll('[data-radix-collection-item]').forEach((el) => {
    el.removeAttribute('data-radix-collection-item');
  });
}

/** 从当前选区构造干净的 HTML 片段。 */
function buildCleanHtml(): string | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) {
    return null;
  }
  const range = sel.getRangeAt(0);
  const holder = document.createElement('div');
  // 用公共祖先的直接子节点重建，保证跨段落选择也完整
  const fragment = range.cloneContents();
  holder.appendChild(fragment);

  // 选区若恰好落在单个文本节点内，cloneContents 只会给文本；
  // 包一层段落，保证 Word 里是一段而不是散字。
  if (!holder.querySelector('*')) {
    const p = document.createElement('p');
    p.textContent = holder.textContent || '';
    holder.innerHTML = '';
    holder.appendChild(p);
  }

  sanitize(holder);
  return `<html><head><meta charset="utf-8"><style>${JGS_EXPORT_CSS}</style></head><body>${holder.innerHTML}</body></html>`;
}

/** 安装（幂等）全局 copy 拦截器。 */
export function installJgsCleanCopy(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }
  const w = window as unknown as Record<string, boolean>;
  if (w[INSTALL_FLAG]) {
    return;
  }
  w[INSTALL_FLAG] = true;

  document.addEventListener(
    'copy',
    (event: ClipboardEvent) => {
      try {
        const root = selectionRoot();
        if (!root) {
          return; // 不在聊天内容区 -> 保持浏览器默认行为
        }
        const sel = window.getSelection();
        const text = sel?.toString() ?? '';
        const html = buildCleanHtml();
        if (!event.clipboardData) {
          return;
        }
        if (text) {
          event.clipboardData.setData('text/plain', text);
        }
        if (html) {
          event.clipboardData.setData('text/html', html);
        }
        event.preventDefault();
        event.stopPropagation();
      } catch {
        // 任何异常都退回浏览器默认复制，绝不影响用户
      }
    },
    true,
  );
}

/** React Hook：在组件树里安装一次。 */
export function useJgsCleanCopy(): void {
  useEffect(() => {
    installJgsCleanCopy();
  }, []);
}

export { JGS_CLEAN_COPY_ATTR };
export default useJgsCleanCopy;
